import { createSlice } from "@reduxjs/toolkit";
import {
  getAdminCategories,
  getAdminSubcategories,
  createAdminCategory,
  createAdminSubcategory,
  updateAdminCategory,
  deleteAdminCategory,
  updateAdminSubcategory,
  deleteAdminSubcategory,
} from "../Thunk/categoryThunk";

const initialState = {
  categories: [],
  subcategories: [],
  loading: false,
  subLoading: false,
  createCategoryLoading: false,
  createSubcategoryLoading: false,
  error: null,
  successMessage: null,
};

const categorySlice = createSlice({
  name: "category",
  initialState,
  reducers: {
    clearCategoryError: (state) => {
      state.error = null;
    },
    clearCategorySuccess: (state) => {
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAdminCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAdminCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories =
          action.payload?.items ||
          action.payload?.data ||
          action.payload?.categories ||
          [];
      })
      .addCase(getAdminCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(getAdminSubcategories.pending, (state) => {
        state.subLoading = true;
      })
      .addCase(getAdminSubcategories.fulfilled, (state, action) => {
        state.subLoading = false;
        state.subcategories =
          action.payload?.items ||
          action.payload?.data ||
          action.payload?.subcategories ||
          [];
      })
      .addCase(getAdminSubcategories.rejected, (state, action) => {
        state.subLoading = false;
        state.error = action.payload;
      })

      .addCase(createAdminCategory.pending, (state) => {
        state.createCategoryLoading = true;
        state.error = null;
      })
      .addCase(createAdminCategory.fulfilled, (state, action) => {
        state.createCategoryLoading = false;
        const newCategory =
          action.payload?.data ||
          action.payload?.item ||
          action.payload;

        if (newCategory?._id || newCategory?.id) {
          state.categories.unshift({
            ...newCategory,
            subcategories: newCategory?.subcategories || [],
          });
        }
        state.successMessage = "Category created successfully";
      })
      .addCase(createAdminCategory.rejected, (state, action) => {
        state.createCategoryLoading = false;
        state.error = action.payload;
      })

      .addCase(createAdminSubcategory.pending, (state) => {
        state.createSubcategoryLoading = true;
        state.error = null;
      })
      .addCase(createAdminSubcategory.fulfilled, (state, action) => {
        state.createSubcategoryLoading = false;

        const newSub =
          action.payload?.data ||
          action.payload?.item ||
          action.payload;

        if (newSub?._id || newSub?.id) {
          state.subcategories.unshift(newSub);

          const categoryId = newSub.categoryId;
          const index = state.categories.findIndex(
            (cat) => String(cat._id || cat.id) === String(categoryId)
          );

          if (index !== -1) {
            const existingSubs = state.categories[index].subcategories || [];
            state.categories[index].subcategories = [...existingSubs, newSub];
          }
        }

        state.successMessage = "Subcategory created successfully";
      })
      .addCase(createAdminSubcategory.rejected, (state, action) => {
        state.createSubcategoryLoading = false;
        state.error = action.payload;
      })

      .addCase(deleteAdminCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAdminCategory.fulfilled, (state, action) => {
        state.loading = false;

        const deletedId = action.payload?.deletedId || action.meta.arg;

        state.categories = (state.categories || []).filter(
          (cat) => String(cat._id || cat.id) !== String(deletedId)
        );

        state.subcategories = (state.subcategories || []).filter(
          (sub) => String(sub.categoryId) !== String(deletedId)
        );

        state.successMessage = "Category deleted successfully";
      })
      .addCase(deleteAdminCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateAdminCategory.pending, (state) => {
        state.createCategoryLoading = true;
        state.error = null;
      })
      .addCase(updateAdminCategory.fulfilled, (state, action) => {
        state.createCategoryLoading = false;
        const id = action.payload?._id;
        if (id) {
          const idx = state.categories.findIndex(
            (c) => String(c._id || c.id) === String(id)
          );
          if (idx !== -1 && action.payload) {
            state.categories[idx] = {
              ...state.categories[idx],
              name: action.payload.name ?? state.categories[idx].name,
              image: action.payload.image ?? state.categories[idx].image,
            };
          }
        }
        state.successMessage = "Category updated successfully";
      })
      .addCase(updateAdminCategory.rejected, (state, action) => {
        state.createCategoryLoading = false;
        state.error = action.payload;
      })

      .addCase(updateAdminSubcategory.pending, (state) => {
        state.createSubcategoryLoading = true;
        state.error = null;
      })
      .addCase(updateAdminSubcategory.fulfilled, (state, action) => {
        state.createSubcategoryLoading = false;
        const sub = action.payload;
        if (sub?._id) {
          const subIdx = state.subcategories.findIndex(
            (s) => String(s._id || s.id) === String(sub._id)
          );
          if (subIdx !== -1) {
            state.subcategories[subIdx] = { ...state.subcategories[subIdx], ...sub };
          }
          const catIdx = state.categories.findIndex(
            (c) => String(c._id || c.id) === String(sub.categoryId)
          );
          if (catIdx !== -1 && Array.isArray(state.categories[catIdx].subcategories)) {
            const scIdx = state.categories[catIdx].subcategories.findIndex(
              (sc) => String(sc._id || sc.uiId) === String(sub._id)
            );
            if (scIdx !== -1) {
              state.categories[catIdx].subcategories[scIdx] = {
                ...state.categories[catIdx].subcategories[scIdx],
                ...sub,
              };
            }
          }
        }
        state.successMessage = "Subcategory updated successfully";
      })
      .addCase(updateAdminSubcategory.rejected, (state, action) => {
        state.createSubcategoryLoading = false;
        state.error = action.payload;
      })

      .addCase(deleteAdminSubcategory.fulfilled, (state, action) => {
        const deletedId = action.payload?.deletedId || action.meta.arg;
        state.subcategories = (state.subcategories || []).filter(
          (s) => String(s._id || s.id) !== String(deletedId)
        );
        state.categories = (state.categories || []).map((cat) => ({
          ...cat,
          subcategories: (cat.subcategories || []).filter(
            (sc) => String(sc._id || sc.uiId) !== String(deletedId)
          ),
        }));
        state.successMessage = "Subcategory deleted successfully";
      })
      .addCase(deleteAdminSubcategory.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { clearCategoryError, clearCategorySuccess } =
  categorySlice.actions;

export default categorySlice.reducer;
